import React, {
    useEffect, useLayoutEffect, useRef, useState
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { mediaQueries, colors } from 'style/config';
import { Text } from 'components/ui';
import SwatchItem from 'components/ProductPage/Swatches/SwatchItem';
import UrlUtils from 'utils/Url';
import utils from 'analytics/utils';
import store from 'store/Store';
import wizardActions from 'actions/WizardActions';

import swatchUtils from 'utils/Swatch';

const {
    getSwatchType, SWATCH_TYPES, SWATCH_BORDER, SQUARE_MARGIN, SWATCH_GROUP_VIEWS
} = swatchUtils;

// useLayoutEffect does nothing on the server and warns during SSR; fall back to useEffect there
const useIsomorphicLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

const getGroupLabelMarginTop = (isListView, isDefaultView, index) => {
    if (isListView) {
        return index > 0 ? 3 : 1;
    }

    if (isDefaultView) {
        return index > 0 ? 3 : 2;
    }

    return index === 0 ? 2 : undefined;
};

const SwatchGroup = props => {
    const {
        index,
        currentProduct,
        showColorMatch,
        skus = [],
        skusWithGroupNames,
        currentSku,
        wizardResult = {},
        wizardMatchText,
        isCustomSet,
        swatchItemCallback,
        scrollRef,
        selectedItemRef,
        customSetDataAt,
        groupName,
        swatchView,
        isFragrance,
        isDropdown,
        colorIQMatch,
        showHorizontalSizeSelector
    } = props;

    const swatchTypeStyle = isFragrance ? 'fragrance' : getSwatchType(isCustomSet ? SWATCH_TYPES.CIRCLE : currentProduct.swatchType);
    const isListView = swatchView === SWATCH_GROUP_VIEWS.LIST;
    const isDefaultView = swatchView === SWATCH_GROUP_VIEWS.DEFAULT;
    const listPadding = isDropdown ? 3 : ['container', 3];
    const isHorizontalLayout = showHorizontalSizeSelector && !isDropdown && swatchUtils.isHorizontalSizeVariationType(currentProduct.variationType);

    const productId = currentProduct?.productDetails?.productId;
    const hasDispatchedRef = useRef(false);

    // Hides the right-edge fade once the row is scrolled to its end
    const [canScrollRight, setCanScrollRight] = useState(false);

    useIsomorphicLayoutEffect(() => {
        if (!isDefaultView) {
            return undefined;
        }

        const node = scrollRef?.current;

        if (!node) {
            return undefined;
        }

        const updateScrollState = () => {
            const maxScroll = node.scrollWidth - node.clientWidth;
            const nextCanScrollRight = maxScroll > 1 && node.scrollLeft < maxScroll - 1;

            setCanScrollRight(prev => (prev === nextCanScrollRight ? prev : nextCanScrollRight));
        };

        let rafId = null;
        const scheduleUpdateScrollState = () => {
            if (rafId !== null) {
                return;
            }

            rafId = requestAnimationFrame(() => {
                rafId = null;
                updateScrollState();
            });
        };

        updateScrollState();

        node.addEventListener('scroll', scheduleUpdateScrollState, { passive: true });
        node.addEventListener('load', scheduleUpdateScrollState, { capture: true, passive: true });
        const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleUpdateScrollState) : null;
        resizeObserver?.observe(node);

        return () => {
            node.removeEventListener('scroll', scheduleUpdateScrollState);
            node.removeEventListener('load', scheduleUpdateScrollState, { capture: true });
            resizeObserver?.disconnect();

            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [isDefaultView, skus.length, productId, scrollRef]);

    useEffect(() => {
        hasDispatchedRef.current = false;
    }, [productId]);

    useEffect(() => {
        if (hasDispatchedRef.current) {
            return;
        }

        const matchedSkuId = UrlUtils.getParamValueAsSingleString('matchedSkuId');

        if (!matchedSkuId) {
            return;
        }

        const matchedSku = skus.find(sku => sku.skuId === matchedSkuId);

        if (!matchedSku) {
            return;
        }

        const { pageName } = utils.getPreviousPageData();

        if (pageName?.match('multi-product shade finder-results page')) {
            hasDispatchedRef.current = true;
            store.dispatch(
                wizardActions.setResult({
                    result: { skuId: matchedSkuId },
                    matchText: wizardMatchText || 'match'
                })
            );
        }
    }, [skus, wizardMatchText, productId]);

    return (
        <div css={props.customCss?.groupWrapper}>
            {groupName && !isHorizontalLayout && (
                <Text
                    is='p'
                    fontSize='sm'
                    lineHeight='tight'
                    marginTop={getGroupLabelMarginTop(isListView, isDefaultView, index)}
                    marginBottom={1}
                    children={groupName}
                    css={{
                        paddingLeft: isListView && listPadding,
                        paddingTop: isListView && listPadding,
                        ...props.customCss?.groupName
                    }}
                />
            )}
            <div
                ref={scrollRef}
                css={[
                    styles.container,
                    swatchView && [styles[swatchView], isListView || styles[swatchView][swatchTypeStyle], isFragrance && styles.fragrance],
                    isHorizontalLayout && styles.horizontalLayout,
                    isDefaultView && canScrollRight && styles.defaultViewFade
                ]}
            >
                {skus.map((sku, i) => {
                    const matchText = wizardMatchText;

                    const skuFinderSelected = wizardResult.skuId === sku.skuId;

                    const itemGroupName = isHorizontalLayout && skusWithGroupNames ? skusWithGroupNames[i]?.groupName : groupName;

                    return (
                        <SwatchItem
                            key={sku.skuId}
                            isSkuReady={props.isSkuReady}
                            swatchView={swatchView}
                            swatchTypeStyle={swatchTypeStyle}
                            isFragrance={isFragrance}
                            selectedItemRef={selectedItemRef}
                            index={i}
                            highlight={skuFinderSelected}
                            matchText={matchText}
                            currentProduct={currentProduct}
                            sku={sku}
                            activeSku={currentSku}
                            showColorMatch={showColorMatch}
                            isShadeFinderActive={Object.keys(wizardResult).length > 0 ? wizardResult : false}
                            isCustomSet={isCustomSet}
                            isDropdown={isDropdown}
                            swatchItemCallback={swatchItemCallback}
                            customSetDataAt={customSetDataAt}
                            listPadding={listPadding}
                            colorIQMatch={colorIQMatch}
                            sizeGroupName={itemGroupName}
                            {...props}
                            showHorizontalSizeSelector={isHorizontalLayout}
                        />
                    );
                })}
            </div>
        </div>
    );
};

const styles = {
    // create new stacking context to prevent swatch dropdown layer conflicts
    container: {
        position: 'relative',
        zIndex: 0
    },
    [SWATCH_GROUP_VIEWS.GRID]: {
        display: 'flex',
        flexWrap: 'wrap',
        marginLeft: -SWATCH_BORDER,
        marginRight: -SWATCH_BORDER,
        paddingLeft: 0,
        paddingRight: 0,
        square: {
            marginRight: -(SWATCH_BORDER - SQUARE_MARGIN)
        },
        // squeeze in 1 more swatch for 375px mobile view to show 8 vs 7 across
        circle: {
            marginRight: -(SWATCH_BORDER + 1)
        }
    },
    [SWATCH_GROUP_VIEWS.DEFAULT]: {
        display: 'flex',
        flexWrap: 'nowrap',
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        // hide the scrollbar on mobile (Firefox + WebKit/Blink); fade + cutoff signal scrollability
        scrollbarWidth: 'none',
        '::-webkit-scrollbar': {
            display: 'none'
        },
        marginLeft: -SWATCH_BORDER,
        marginRight: -SWATCH_BORDER,
        square: {
            marginRight: -(SWATCH_BORDER - SQUARE_MARGIN)
        },
        circle: {
            marginRight: -(SWATCH_BORDER + 1)
        }
    },
    defaultViewFade: {
        WebkitMaskImage: `linear-gradient(to right, ${colors.black} 64%, transparent 100%)`,
        maskImage: `linear-gradient(to right, ${colors.black} 64%, transparent 100%)`
    },
    fragrance: {
        [mediaQueries.md]: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)'
        }
    },
    horizontalLayout: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0,
        marginLeft: 0,
        marginRight: 0,
        paddingBottom: 0
    }
};

SwatchGroup.shouldUpdatePropsOn = [
    'skus',
    'currentSku.skuId',
    'currentProduct.currentSku',
    'currentProduct.hoveredSku',
    'colorIQMatch',
    'showHorizontalSizeSelector'
];

export default wrapFunctionalComponent(SwatchGroup, 'SwatchGroup');
